import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const [stats, setStats] = useState({
    leads: { total: 0, new: 0 },
    invoices: { total: 0, pending: 0, paid: 0, overdue: 0, totalAmount: 0 },
    income: { total: 0, thisMonth: 0 },
    expenses: { total: 0, thisMonth: 0 },
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [leadsRes, invoicesRes, incomeRes, expensesRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/income`),
        axios.get(`${API}/expenses`),
      ]);

      const leads = leadsRes.data;
      const invoices = invoicesRes.data;
      const income = incomeRes.data;
      const expenses = expensesRes.data;

      const thisMonth = new Date().getMonth();
      const thisYear = new Date().getFullYear();

      setStats({
        leads: {
          total: leads.length,
          new: leads.filter(l => l.status === 'new').length,
        },
        invoices: {
          total: invoices.length,
          pending: invoices.filter(i => i.status === 'pending').length,
          paid: invoices.filter(i => i.status === 'paid').length,
          overdue: invoices.filter(i => i.status === 'overdue').length,
          totalAmount: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
        },
        income: {
          total: income.reduce((sum, i) => sum + i.amount, 0),
          thisMonth: income
            .filter(i => {
              const d = new Date(i.date);
              return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
            })
            .reduce((sum, i) => sum + i.amount, 0),
        },
        expenses: {
          total: expenses.reduce((sum, e) => sum + e.amount, 0),
          thisMonth: expenses
            .filter(e => {
              const d = new Date(e.date);
              return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
            })
            .reduce((sum, e) => sum + e.amount, 0),
        },
      });

      setRecentLeads(leads.slice(0, 5));
      setRecentInvoices(invoices.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const netProfit = stats.income.total - stats.expenses.total;
  const netProfitThisMonth = stats.income.thisMonth - stats.expenses.thisMonth;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl text-white font-medium">Dashboard</h1>
        <p className="text-gray-400">Welcome back! Here's your business overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/leads" className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5 hover:border-purple-500/30 transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Leads</span>
          </div>
          <div className="text-3xl text-white font-bold">{stats.leads.total}</div>
          <div className="text-sm text-blue-400 mt-1">{stats.leads.new} new</div>
        </Link>

        <Link to="/admin/invoices" className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5 hover:border-purple-500/30 transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Invoices</span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(stats.invoices.totalAmount)}</div>
          <div className="text-sm text-yellow-400 mt-1">{stats.invoices.pending} pending</div>
        </Link>

        <Link to="/admin/income" className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5 hover:border-purple-500/30 transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Income</span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(stats.income.total)}</div>
          <div className="flex items-center gap-1 text-sm text-green-400 mt-1">
            <ArrowUpRight className="w-4 h-4" />
            {formatCurrency(stats.income.thisMonth)} this month
          </div>
        </Link>

        <Link to="/admin/expenses" className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5 hover:border-purple-500/30 transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Expenses</span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(stats.expenses.total)}</div>
          <div className="flex items-center gap-1 text-sm text-red-400 mt-1">
            <ArrowDownRight className="w-4 h-4" />
            {formatCurrency(stats.expenses.thisMonth)} this month
          </div>
        </Link>
      </div>

      {/* Net Profit Card */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-2xl border border-purple-500/20 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm mb-1">Net Profit (YTD)</p>
            <p className={`text-4xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(netProfit)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-gray-400 text-sm mb-1">This Month</p>
            <p className={`text-2xl font-bold ${netProfitThisMonth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(netProfitThisMonth)}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Recent Leads</h3>
            <Link to="/admin/leads" className="text-purple-400 text-sm hover:underline">View all</Link>
          </div>
          {recentLeads.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No leads yet</p>
          ) : (
            <div className="space-y-3">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs">
                      {lead.first_name?.[0]}{lead.last_name?.[0]}
                    </div>
                    <div>
                      <p className="text-white text-sm">{lead.first_name} {lead.last_name}</p>
                      <p className="text-gray-500 text-xs">{lead.company}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    lead.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                    lead.status === 'qualified' ? 'bg-green-500/20 text-green-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {lead.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Recent Invoices</h3>
            <Link to="/admin/invoices" className="text-purple-400 text-sm hover:underline">View all</Link>
          </div>
          {recentInvoices.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No invoices yet</p>
          ) : (
            <div className="space-y-3">
              {recentInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div>
                    <p className="text-white text-sm">#{invoice.invoice_number}</p>
                    <p className="text-gray-500 text-xs">{invoice.client_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-sm">{formatCurrency(invoice.total)}</p>
                    <span className={`text-xs ${
                      invoice.status === 'paid' ? 'text-green-400' :
                      invoice.status === 'overdue' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
